import { useEffect, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { endOfDay, startOfDay } from 'date-fns'

import { PlusIcon } from '@gto/icons-react/outline/plus'
import { UsersIcon } from '@gto/icons-react/outline/users'
import { Button } from '@gto/ui/button'
import type { PaginationState, SortingState } from '@gto/ui/data-table'
import { DateRangePicker } from '@gto/ui/date-picker'

import type { AdminAffiliateTierSchema } from '@/api/gen'
import { getListPartnersQueryKey, useCreatePartner, useListPartners, useListTiers } from '@/api/gen'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { SearchInput } from '@/components/form'
import { Page } from '@/components/page'
import { PartnersFilterBar } from '@/components/partners-filter-bar'
import { usePartnerColumns } from '@/components/partners-table'
import { StatsCard } from '@/components/stats-card'
import { useClampPageIntoRange } from '@/hooks/use-clamp-page-into-range'
import { useDateShortcuts } from '@/hooks/use-date-shortcuts'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { fromDateStr, toDateStr } from '@/utils/date-str'
import { extractValidationFieldErrors } from '@/utils/extract-validation-detail'
import type { PartnersSearch, SortField } from '@/utils/partners-search-params'
import { SORT_FIELDS, toListPartnersParams } from '@/utils/partners-search-params'

import { NewPartnerDialog } from './new-partner-dialog'

const DEFAULT_PAGE_SIZE = 25

const isSortField = (s: string): s is SortField => (SORT_FIELDS as readonly string[]).includes(s)

// Route API handle — the route exports its `validateSearch` schema, so
// `Route.useSearch()` here is fully typed against `PartnersSearch`.
const partnersRoute = getRouteApi('/_private/admin/partners')

export const PartnersPage = () => {
  const columns = usePartnerColumns()
  const { formatMessage } = useIntl()
  // Route-scoped navigate: types the `search` reducer against PartnersSearch
  // (the global useNavigate would union every route's search schema).
  const navigate = partnersRoute.useNavigate()
  const shortcuts = useDateShortcuts()

  // ─── URL state is the single source of truth ──────────────────────────────
  // Every filter, sort, and page lives in the URL. No parallel useState —
  // the page is shareable, refresh-proof, and works with back/forward.
  const search = partnersRoute.useSearch()

  const updateSearch = (delta: Partial<PartnersSearch>, opts?: { resetPage?: boolean }) => {
    void navigate({
      to: '/admin/partners',
      search: (prev: PartnersSearch) => {
        const next = { ...prev, ...delta }
        if (opts?.resetPage !== false) {
          // Narrowing a filter must reset to page 1 — otherwise the current
          // page index could point past the end of the new result. Pagination
          // changes opt out via { resetPage: false }.
          next.page = undefined
        }

        return next
      },
      replace: true,
    })
  }

  // The search input renders a local draft and pushes it into the URL — and
  // with it the Orval query key, i.e. a BE round-trip — only after the typist
  // pauses. Same pattern as admin-orders-page.
  const [qDraft, setQDraft] = useState(search.q ?? '')
  const { debounced: writeQDraft } = useDebouncedCallback((value: string) => {
    updateSearch({ q: value === '' ? undefined : value })
  }, 300)

  // Keep the draft following external URL changes (back/forward, drill-down
  // links). Writes never originate here, so this cannot loop with the
  // debounced write above.
  useEffect(
    function syncDraftWithSearchQuery() {
      setQDraft(search.q ?? '')
    },
    [search.q],
  )

  // ─── Tier list — needed by the facet bar AND the active-chip resolver ─────
  const tiersQuery = useListTiers({ query: { staleTime: 60_000 } })
  const tierOptions: AdminAffiliateTierSchema[] = tiersQuery.data ?? []

  // A tiers-load failure silently empties the Tier facet — no Sentry signal
  // otherwise (the partner list still works, so the page does not surface an
  // error state). Forward terminal failures for parity with the partners
  // query above.
  useReportQueryError(tiersQuery, 'listTiers failed: ')

  // ─── New partner ───────────────────────────────────────────────────────────
  const queryClient = useQueryClient()
  const [newPartnerOpen, setNewPartnerOpen] = useState(false)
  const { onError: reportCreatePartnerError } = useMutationErrorHandler('createPartner failed: ')
  const createPartner = useCreatePartner({
    mutation: {
      onSuccess: (created) => {
        void queryClient.invalidateQueries({ queryKey: getListPartnersQueryKey() })
        setNewPartnerOpen(false)
        void navigate({
          to: '/admin/partners/$partnerId',
          params: { partnerId: created.id },
          search: { origin: 'partners' },
        })
      },
      // A field-scoped 422 is the admin's input to correct, shown on the field.
      // Every other failure is a fault and reaches Sentry; the dialog banner
      // shows it either way.
      onError: (error) => {
        if (extractValidationFieldErrors(error) === null) {
          reportCreatePartnerError(error)
        }
      },
    },
  })

  // ─── Partner list query (URL → BE params) ─────────────────────────────────
  const listParams = toListPartnersParams(search, DEFAULT_PAGE_SIZE)
  const partnersQuery = useListPartners(listParams, {
    // keepPreviousData keeps the prior page rendered while the next page loads,
    // so paging and filter changes don't blank the table between fetches.
    query: { placeholderData: keepPreviousData },
  })
  const { data, isLoading, isError, isPlaceholderData, refetch } = partnersQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global
  // sink. Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(partnersQuery, 'listPartners failed: ')

  const rows = data?.partners ?? []
  const rowCount = data?.total ?? 0
  const counts = data?.status_counts

  // A `?page=` past the end answers empty for a set that is not empty — and the empty
  // state below keys on the rows in hand, so it would replace the table (and its pager)
  // rather than let the admin page back. Walk the URL to the last populated page instead.
  useClampPageIntoRange({
    page: search.page ?? 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: rowCount,
    loading: isLoading || isPlaceholderData,
    onClamp: (page) => updateSearch({ page }, { resetPage: false }),
  })

  // ─── Table pagination + sort models ───────────────────────────────────────
  const paginationModel: PaginationState = {
    pageIndex: (search.page ?? 1) - 1, // URL is 1-based, the table is 0-based
    pageSize: DEFAULT_PAGE_SIZE,
  }

  const sortModel: SortingState =
    search.sort === undefined ? [] : [{ id: search.sort, desc: (search.dir ?? 'desc') === 'desc' }]

  // ─── Subtitle ─────────────────────────────────────────────────────────────
  const subtitle =
    counts == null ? undefined : (
      <FormattedMessage
        id="admin.partners.subtitle"
        values={{
          total: <FormattedNumber value={counts.total} />,
          pending: (
            <span className="font-medium text-warning-main">
              <FormattedMessage
                id="admin.partners.subtitlePending"
                values={{ count: <FormattedNumber value={counts.pending_approval} /> }}
              />
            </span>
          ),
        }}
      />
    )

  // ─── Date range — URL stores YYYY-MM-DD; UI consumes Date pairs ───────────
  const dateRange: [Date | null, Date | null] = [
    search.from === undefined ? null : fromDateStr(search.from),
    search.to === undefined ? null : fromDateStr(search.to),
  ]

  return (
    <Page>
      <Page.Header
        subtitle={subtitle}
        title={<FormattedMessage id="admin.partners.title" />}
        actions={
          <Button
            data-tst="new-partner-open"
            variant="contained"
            startIcon={
              <PlusIcon
                height={18}
                width={18}
              />
            }
            onClick={() => {
              createPartner.reset()
              setNewPartnerOpen(true)
            }}
          >
            <FormattedMessage id="admin.partners.new" />
          </Button>
        }
      />
      <NewPartnerDialog
        open={newPartnerOpen}
        serverError={createPartner.error}
        submitting={createPartner.isPending}
        tiers={tierOptions}
        onClose={() => setNewPartnerOpen(false)}
        onSubmit={(body) => createPartner.mutate({ data: body })}
      />

      <Page.Content>
        {/* Single filter row: facet selects (status, tier, account, segment)
            left, search + date range right — plus the active-chip row. */}
        <PartnersFilterBar
          search={search}
          statusCounts={counts}
          tiers={tierOptions.map((t) => ({ id: t.id, name: t.name }))}
          endSlot={
            <>
              <SearchInput
                placeholder={formatMessage({ id: 'admin.partners.search' })}
                value={qDraft}
                onChange={(e) => {
                  setQDraft(e.target.value)
                  writeQDraft(e.target.value)
                }}
              />
              <DateRangePicker
                closeOnSelect={false}
                presets={shortcuts}
                value={dateRange}
                onChange={([newFrom, newTo], context) => {
                  if (context.validationError.some(Boolean)) {
                    return
                  }
                  updateSearch({
                    from: newFrom === null ? undefined : toDateStr(startOfDay(newFrom)),
                    to: newTo === null ? undefined : toDateStr(endOfDay(newTo)),
                  })
                }}
              />
            </>
          }
          onChange={(delta) => updateSearch(delta)}
          onClearFacets={() =>
            updateSearch({
              status: undefined,
              tier: undefined,
              account: undefined,
              beta: undefined,
              fullyMigrated: undefined,
              termsAccepted: undefined,
              country: undefined,
              segment: undefined,
            })
          }
        />

        {isError ? (
          <EmptyState
            description={<FormattedMessage id="admin.partners.error.description" />}
            title={<FormattedMessage id="admin.partners.error.title" />}
            action={
              <Button
                color="primary"
                data-tst="partners-retry"
                variant="outlined"
                onClick={() => {
                  void refetch()
                }}
              >
                <FormattedMessage id="admin.partners.error.retry" />
              </Button>
            }
          />
        ) : !isLoading && rows.length === 0 ? (
          <EmptyState title={<FormattedMessage id="admin.partners.empty" />} />
        ) : (
          <StatsCard
            icon={<UsersIcon />}
            title={<FormattedMessage id="admin.partners.tableTitle" />}
          >
            <DataTable
              manualPagination
              manualSorting
              columns={columns}
              data={rows}
              getRowId={(row) => row.id}
              loading={isLoading}
              pagination={paginationModel}
              // The outgoing page stays on screen through a page/sort/filter change
              // (`keepPreviousData`), which is a refetch, not a first load — see the note on
              // the wrapper's two signals.
              refetching={isPlaceholderData}
              rowCount={rowCount}
              sorting={sortModel}
              onPaginationChange={(next) => {
                updateSearch({ page: next.pageIndex + 1 }, { resetPage: false })
              }}
              onRowClick={(row) => {
                void navigate({
                  to: '/admin/partners/$partnerId',
                  params: { partnerId: row.id },
                  // Mark the origin so the detail's "Back to partners" control
                  // steps back to THIS exact filtered/paged/sorted view instead
                  // of the bare list (MKT-439). Other entry points omit it.
                  search: { origin: 'partners' },
                })
              }}
              onSortingChange={(next) => {
                const first = next[0]
                if (first === undefined || !isSortField(first.id)) {
                  // User cleared the sort — drop both keys from the URL so the
                  // page reverts to the BE default ordering (-created_at).
                  updateSearch({ sort: undefined, dir: undefined })

                  return
                }
                updateSearch({ sort: first.id, dir: first.desc ? 'desc' : 'asc' })
              }}
            />
          </StatsCard>
        )}
      </Page.Content>
    </Page>
  )
}
